'use client';

import { COLOR_SCHEMES, MEDIA_QUERY } from './constants';
import type { AppUiThemeProps, ThemeScriptProps } from './types';

/**
 * @description disable all animations on a page by injecting a style tag with a transition override
 * @returns a function to re-enable animations
 */
export const disableAnimation = () => {
  const css = document.createElement('style');
  css.appendChild(
    document.createTextNode(
      `*{-webkit-transition:none!important;-moz-transition:none!important;-o-transition:none!important;-ms-transition:none!important;transition:none!important}`
    )
  );
  document.head.appendChild(css);

  return () => {
    // force restyle
    (() => global.getComputedStyle(document.body))();

    // cleanup
    setTimeout(() => {
      document.head.removeChild(css);
    }, 1);
  };
};

type MediaQueryEventHandler = (
  this: MediaQueryList,
  ev: MediaQueryListEventMap['change']
) => unknown;

/**
 * @description register a listener for system theme changes
 */
export const registerForSystemThemeChange = (
  mediaQueryList: MediaQueryList,
  eventHandler: MediaQueryEventHandler
): void => {
  // use deprecated `addListener` to support Safari < 14
  if (mediaQueryList.addListener) {
    mediaQueryList.addListener(eventHandler);
  } else {
    mediaQueryList.addEventListener(`change`, eventHandler);
  }
};

/**
 * @description deregister a listener for system theme changes
 */
export const deregisterForSystemThemeChange = (
  mediaQueryList: MediaQueryList,
  eventHandler: MediaQueryEventHandler
): void => {
  // use deprecated `removeListener` to support Safari < 14
  if (mediaQueryList.removeListener) {
    mediaQueryList.removeListener(eventHandler);
  } else {
    mediaQueryList.removeEventListener(`change`, eventHandler);
  }
};

/**
 * @description get the system theme preference to help resolve the active theme
 */
export const getSystemTheme = (evt?: MediaQueryList | MediaQueryListEvent) => {
  const mq = evt ?? global.matchMedia(MEDIA_QUERY);
  const isDark = mq.matches;
  return isDark ? `dark` : `light`;
};

/**
 * @description update the DOM with the active theme
 */
export const updateDomThemeAttributes = (
  themeNameValue: string | string[],
  listOfThemeValues: ThemeScriptProps['attrs'],
  fallbackTheme: AppUiThemeProps['fallbackTheme'],
  resolved: AppUiThemeProps['fallbackTheme']
) => {
  // disable animations while the theme is being updated
  const restoreAnimation = disableAnimation();
  // handle setting the top-level color scheme style rule
  const docEl = document.documentElement;
  const docBody = document.body;

  // handle setting the theme attributes to class names
  docEl.classList.remove(...listOfThemeValues);
  docBody.classList.remove(...listOfThemeValues);
  if (Array.isArray(themeNameValue)) {
    docEl.classList.add(...themeNameValue);
    docBody.classList.add(...themeNameValue);
  } else if (themeNameValue) {
    docEl.classList.add(themeNameValue);
    docBody.classList.add(themeNameValue);
  }

  // handle setting the top-level color scheme style rule
  const fallback =
    fallbackTheme !== undefined &&
    (COLOR_SCHEMES as ReadonlyArray<string>).includes(fallbackTheme)
      ? fallbackTheme
      : null;

  const colorScheme =
    resolved !== undefined &&
    (COLOR_SCHEMES as ReadonlyArray<string>).includes(resolved)
      ? resolved
      : fallback;
  docEl.style.colorScheme = colorScheme ?? '';

  // enable animations after the theme is updated
  restoreAnimation();
};
